import type { SchoolData } from '@/context/SchoolContext';
import type { TasklistPageBlock, TasklistPageJson, TasklistPageSection } from '@/types/tasklistPageJson';
import schoolOverviewPage400Template from '../../public/data/pages/400.json';
import { mergeSchoolOverviewPageFromWp } from '@/utils/tasklistPageSchoolOverviewMerge';

export type SchoolOverviewQuickLinkOptions = {
  /** WP single-school API `calendar_link` */
  calendarLink?: string;
  schoolAbbreviation?: string;
};

export function buildSchoolOverviewQuickLinksSection(
  options: SchoolOverviewQuickLinkOptions
): TasklistPageSection | null {
  const calendar = options.calendarLink?.trim();
  if (!calendar) return null;

  const abbr = options.schoolAbbreviation?.trim() || '{school}';
  const blocks: TasklistPageBlock[] = [
    {
      type: 'quickLink',
      heading: `${abbr} Academic Calendar`,
      buttonUrl: calendar,
      buttonLabel: 'See the calendar',
    },
  ];

  return {
    title: 'Quick Links',
    blocks,
    displayConditions: { userRole: [], gradeBand: [] },
  };
}

/** Prepends Quick Links when WP `calendar_link` is available (page `400`). */
export function prependSchoolOverviewQuickLinks(
  page: TasklistPageJson,
  options: SchoolOverviewQuickLinkOptions
): TasklistPageJson {
  const quick = buildSchoolOverviewQuickLinksSection(options);
  if (!quick) return page;

  const content = page.content ?? [];
  if (content.some((s) => s.title.trim() === 'Quick Links')) return page;

  return { ...page, content: [quick, ...content] };
}

export type BuildSchoolOverviewFromOnboardingOptions = {
  isPrivateSchool: boolean;
  hasEnrolledFlex: boolean;
  userRoleVal: string | undefined;
  quickLinks: SchoolOverviewQuickLinkOptions;
};

/**
 * When `checklist_secondary_pages["400"]` has no parsable WP payload, builds the school overview
 * from the static `400.json` shell plus live school API fields (same pipeline as WP merge).
 */
export function buildSchoolOverviewPageFromOnboarding(
  school: SchoolData,
  opts: BuildSchoolOverviewFromOnboardingOptions
): TasklistPageJson | null {
  if (!school.onboarding_content) return null;

  const template = schoolOverviewPage400Template as TasklistPageJson;
  const merged = mergeSchoolOverviewPageFromWp(template, school, {
    isPrivateSchool: opts.isPrivateSchool,
    hasEnrolledFlex: opts.hasEnrolledFlex,
    userRoleVal: opts.userRoleVal,
  });
  return prependSchoolOverviewQuickLinks(merged, opts.quickLinks);
}
